import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Segmented, Spin, Alert, Button, Typography, Space, Badge, theme as antdTheme } from 'antd';
import {
  TeamOutlined, CrownOutlined, ThunderboltOutlined, RiseOutlined,
  UserAddOutlined, LoginOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import axios from 'axios';

const { Text } = Typography;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const FREE_COLOR = '#94a3b8'; // slate
const PRO_COLOR  = '#0a40d9'; // brand indigo

const RANGES = [
  { label: 'Today',   value: 'today' },
  { label: '7 days',  value: 7  },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: 'All',     value: 'all' },
];

/* ── Stat tile ── */
const StatCard = ({ title, value, sub, icon, color }) => {
  const { token: tk } = antdTheme.useToken();
  return (
    <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12, color: tk.colorTextSecondary, marginBottom: 6, fontWeight: 500 }}>{title}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1, fontFamily: 'var(--font-mono)', letterSpacing: '-0.03em' }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: tk.colorTextTertiary, marginTop: 5 }}>{sub}</div>}
        </div>
        <div style={{ width: 42, height: 42, borderRadius: 10, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {React.cloneElement(icon, { style: { fontSize: 20, color } })}
        </div>
      </div>
    </Card>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{dayjs(label).format('MMM D, YYYY')}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></div>
      ))}
    </div>
  );
};

const splitSub = (a) => `${a?.free ?? 0} free · ${a?.pro ?? 0} pro`;

const AdminEngagement = () => {
  const { token: tk } = antdTheme.useToken();
  const [days, setDays]       = useState(30);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [realtime, setRealtime] = useState(null);

  const fetchData = useCallback(async (d) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`/api/admin/engagement?days=${d}`, { headers: authHeader() });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load engagement analytics');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(days); }, [days, fetchData]);

  // Poll the realtime "active now" counter every 20s
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await axios.get('/api/admin/engagement/realtime', { headers: authHeader() });
        if (!stop) setRealtime(r.data);
      } catch { /* ignore transient polling errors */ }
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const rangeShort = days === 'today' ? 'today' : days === 'all' ? 'all time' : `${days}d`;
  const t = data?.totals  || {};
  const a = data?.active  || {};
  const daily = (data?.daily || []).map(x => ({ ...x, label: x.date }));
  const totalSignups = daily.reduce((s, x) => s + (x.signups || 0), 0);
  const totalLogins  = daily.reduce((s, x) => s + (x.logins  || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <Text style={{ fontSize: 18, fontWeight: 700, display: 'block' }}>Engagement</Text>
          <Text style={{ color: 'var(--text-2)', fontSize: 13 }}>Active customers and traffic, split by plan.</Text>
        </div>
        <Space>
          <Segmented options={RANGES} value={days} onChange={setDays} />
          <Button icon={<ReloadOutlined />} onClick={() => fetchData(days)} />
        </Space>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 8 }} />}

      {/* ── Realtime "active now" banner (polls every 20s) ── */}
      <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 16 }} styles={{ body: { padding: '14px 20px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Space size={10}>
            <Badge status="processing" />
            <div>
              <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>Active right now</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>users active in the last {realtime?.window_minutes ?? 5} minutes · refreshes every 20s</div>
            </div>
          </Space>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'var(--font-mono)', color: '#10b981', lineHeight: 1 }}>{realtime ? realtime.total : '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>{realtime?.free ?? 0} free · {realtime?.pro ?? 0} pro</div>
          </div>
        </div>
      </Card>

      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
      ) : (
        <>
          {/* ── Active-user stat cards ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard title="Total customers" value={t.total_orgs ?? 0} sub={`${t.free_orgs ?? 0} free · ${t.pro_orgs ?? 0} pro`} icon={<TeamOutlined />} color="#0a40d9" />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard title="Active today" value={a.dau?.total ?? 0} sub={splitSub(a.dau)} icon={<ThunderboltOutlined />} color="#10b981" />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard title="Active this week" value={a.wau?.total ?? 0} sub={splitSub(a.wau)} icon={<RiseOutlined />} color="#0ea5e9" />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard title="Active this month" value={a.mau?.total ?? 0} sub={splitSub(a.mau)} icon={<CrownOutlined />} color="#f59e0b" />
            </Col>
          </Row>

          {/* ── Daily active customers (free vs pro) ── */}
          <Card
            title={<Space><RiseOutlined style={{ color: '#0a40d9' }} />Active customers per day</Space>}
            style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 20 }}
            extra={<Text style={{ fontSize: 12, color: 'var(--text-3)' }}>{days === 'today' ? 'today' : days === 'all' ? 'all time' : `last ${days} days`}</Text>}
          >
            {daily.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>No activity in this period</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={daily} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gFree" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={FREE_COLOR} stopOpacity={0.5} /><stop offset="95%" stopColor={FREE_COLOR} stopOpacity={0.05} /></linearGradient>
                    <linearGradient id="gPro" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={PRO_COLOR} stopOpacity={0.5} /><stop offset="95%" stopColor={PRO_COLOR} stopOpacity={0.05} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tk.colorBorderSecondary} vertical={false} />
                  <XAxis dataKey="label" tickFormatter={(d) => dayjs(d).format('MMM D')} tick={{ fontSize: 11, fill: tk.colorTextTertiary }} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: tk.colorTextTertiary }} tickLine={false} axisLine={false} width={40} />
                  <RTooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="pro"  name="Pro"  stackId="1" stroke={PRO_COLOR}  fill="url(#gPro)"  strokeWidth={2} />
                  <Area type="monotone" dataKey="free" name="Free" stackId="1" stroke={FREE_COLOR} fill="url(#gFree)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* ── Traffic: signups + logins ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: 4 }}>
            <Col xs={24} sm={12}>
              <StatCard title={`New signups · ${rangeShort}`} value={totalSignups} sub="organisations created" icon={<UserAddOutlined />} color="#22c55e" />
            </Col>
            <Col xs={24} sm={12}>
              <StatCard title={`Logins · ${rangeShort}`} value={totalLogins} sub="sign-in events — traffic proxy" icon={<LoginOutlined />} color="#2e6bf5" />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default AdminEngagement;
